import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useDirtyFields } from '@/dialogs/item-editor/hooks/useDirtyFields';
import { useItemChangedRealTimeMessaging } from '@/dialogs/item-editor/hooks/useItemChangedRealTimeMessaging';
import {
    buildTempItemKey,
    getOrCreateTabId,
    readTempItem,
    removeTempItem,
    writeTempItem,
} from '@/dialogs/item-editor/utils/tempItemStorage';
import { useExcludedFromSaveFields } from '@/hooks/useExcludedFromSaveFields';

type UseItemReloadFlowParams = {
    selectedPrimaryKey?: string | null;
    refreshItem: () => void;
    persistedItem?: any;
    isLoadingPersistedItem?: boolean;
};

export function useItemReloadFlow({ selectedPrimaryKey, refreshItem, persistedItem, isLoadingPersistedItem }: UseItemReloadFlowParams) {
    const { setValue } = useFormContext();
    const { isDirty, getDirtyFieldsWithValues } = useDirtyFields();
    const { removeExcludedField } = useExcludedFromSaveFields();

    const [openReloadEditorDialog, setOpenReloadEditorDialog] = useState(false);
    const [reloadItemMessage, setReloadItemMessage] = useState('');

    // Per-tab + per-item storage key so drafts in different tabs (or for
    // different items in the same tab) never collide. See LAX-9133.
    const tempItemKey = useMemo(
        () => buildTempItemKey(getOrCreateTabId(), selectedPrimaryKey),
        [selectedPrimaryKey],
    );

    const persistDirtyChangesToLocalStorage = useCallback(() => {
        if (!isDirty) return;

        const editedItem: Record<string, unknown> = getDirtyFieldsWithValues();
        editedItem.primaryKey__ = selectedPrimaryKey ?? null;

        writeTempItem(tempItemKey, editedItem);
    }, [getDirtyFieldsWithValues, isDirty, selectedPrimaryKey, tempItemKey]);

    const clearTempItem = useCallback(() => {
        removeTempItem(tempItemKey);
    }, [tempItemKey]);

    const handleConfirmReload = useCallback(() => {
        // persist current unsaved changes so we can restore them after reopen
        persistDirtyChangesToLocalStorage();

        // close the reload dialog
        setOpenReloadEditorDialog(false);
        setTimeout(() => refreshItem(), 0);

    }, [persistDirtyChangesToLocalStorage, refreshItem]);

    // Hook up real-time item change messaging
    useItemChangedRealTimeMessaging({
        primaryKey: selectedPrimaryKey,
        onReloadDialog: (message: string, open: boolean) => {
            setReloadItemMessage(message);
            setOpenReloadEditorDialog(open);
        },
    });

    // Restore tmpItem if there is one for this (tab, primaryKey) pair.
    useEffect(() => {
        if (!selectedPrimaryKey || isLoadingPersistedItem) return;

        const parsedTmpItem = readTempItem(tempItemKey);
        if (!parsedTmpItem) return;

        // Wait for persistedItem to be loaded and form to be ready
        // Use setTimeout to ensure form is ready and persistedItem effect has run
        setTimeout(() => {
            Object.keys(parsedTmpItem).forEach((attributeName: string) => {
                if (attributeName === 'primaryKey__' || attributeName === '__updatedAt') {
                    return;
                }
                const valueToRestore = parsedTmpItem[attributeName];
                // Remove from excluded fields since we're restoring user's own changes
                removeExcludedField(attributeName);

                // If value is undefined or null, empty the field; otherwise restore the value
                const finalValue = (valueToRestore !== undefined && valueToRestore !== null)
                    ? valueToRestore
                    : '';

                // Set value with shouldDirty: true to mark as user's changes
                setValue(attributeName, finalValue, {
                    shouldDirty: true
                });
            });
            removeTempItem(tempItemKey);
        }, 200);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPrimaryKey, persistedItem, isLoadingPersistedItem, removeExcludedField, setValue, tempItemKey]);

    return {
        openReloadEditorDialog,
        reloadItemMessage,
        handleConfirmReload,
        setOpenReloadEditorDialog,
        setReloadItemMessage,
        clearTempItem,
    } as const;
}
